<template>
  <div class="language-selector">
    <button
      type="button"
      :class="triggerClass"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      :aria-controls="dialogId"
      data-conversion-event="language_selector_open"
      :data-conversion-label="activeLocale.nativeName"
      :data-conversion-location="mobile ? 'header-mobile' : 'header-desktop'"
      @click="openSelector"
    >
      <svg
        class="h-4 w-4 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
      </svg>
      <!-- Prerendered as English; the localized-editions step writes each edition's
           own name here (data-locale-name), and nobody translates language names. -->
      <span translate="no" data-locale-name>{{ activeLocale.nativeName }}</span>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="language-overlay"
        @click.self="closeSelector"
      >
        <section
          ref="dialog"
          :id="dialogId"
          class="language-dialog-panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`${dialogId}-title`"
        >
          <div class="language-dialog-header">
            <h2 :id="`${dialogId}-title`" class="language-dialog-title">
              Select your language
            </h2>
            <button
              ref="closeButton"
              type="button"
              class="language-close"
              aria-label="Close language selector"
              @click="closeSelector"
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="language-options" translate="no">
            <a
              v-for="locale in orderedLocales"
              :key="locale.code"
              :href="languageHref(locale.code)"
              class="language-option"
              :class="{ 'language-option-active': locale.code === activeLocaleCode }"
              data-conversion-event="language_select"
              :data-conversion-label="locale.nativeName"
              :data-conversion-location="mobile ? 'language-modal-mobile' : 'language-modal-desktop'"
              @click="closeSelector"
            >
              <span>{{ locale.nativeName }}</span>
              <svg
                v-if="locale.code === activeLocaleCode"
                class="h-4 w-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </a>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { getLocaleFromPath } from '~/data/locales';
import { dialogFocusTargets, tabWrapTarget } from '~/utils/dialog-focus.mjs';

const props = defineProps({
  mobile: {
    type: Boolean,
    default: false,
  },
  tone: {
    type: String,
    default: 'light',
  },
});

const { basePath, currentLocale, currentLocaleCode, defaultLocale, localePath, locales, switcherLocales } =
  useWorkspaceLocale();

// The default locale (English) is always the first option; the rest keep
// their existing order.
// Dropped languages (data/locale-tiers.mjs) are not offered: their links resolve to English.
const orderedLocales = computed(() => [
  defaultLocale,
  ...switcherLocales.filter((locale) => locale.code !== defaultLocale.code),
]);

const isOpen = ref(false);
const dialog = ref(null);
const closeButton = ref(null);
let openingTrigger = null;
const browserLocaleCode = ref('');
const activeLocaleCode = computed(
  () => browserLocaleCode.value || currentLocaleCode.value,
);
const activeLocale = computed(
  () => locales.find((locale) => locale.code === activeLocaleCode.value) || currentLocale.value,
);
const dialogId = computed(() =>
  props.mobile ? 'mobile-language-dialog' : 'desktop-language-dialog',
);

const triggerClass = computed(() => [
  'language-trigger',
  props.mobile ? 'language-trigger-mobile' : 'language-trigger-desktop',
  props.tone === 'light' ? 'language-trigger-light' : 'language-trigger-dark',
]);

const openSelector = async (event) => {
  if (isOpen.value) return;
  openingTrigger = event.currentTarget;
  isOpen.value = true;
  await nextTick();
  if (isOpen.value) closeButton.value?.focus();
};

const closeSelector = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  const trigger = openingTrigger;
  openingTrigger = null;
  if (trigger?.isConnected) trigger.focus();
};

const languageHref = (localeCode) => localePath(basePath.value, localeCode);

const handleKeydown = (event) => {
  if (!isOpen.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeSelector();
  } else if (event.key === 'Tab') {
    const targets = dialogFocusTargets(dialog.value);
    const target = tabWrapTarget(targets, document.activeElement, event.shiftKey);
    if (target || !targets.length) {
      event.preventDefault();
      target?.focus();
    }
  }
};

onMounted(() => {
  browserLocaleCode.value = getLocaleFromPath(window.location.pathname).code;
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  isOpen.value = false;
  openingTrigger = null;
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.language-selector {
  display: inline-flex;
}

.language-trigger {
  align-items: center;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: 0.375rem;
  line-height: 1;
  outline: none;
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease;
  user-select: none;
  white-space: nowrap;
}

.language-trigger-desktop {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.language-trigger-mobile {
  position: relative;
  font-size: 0.75rem;
  padding: 0.5rem;
}

/* 32px tall, 44px tap area: the extra height overlaps empty header space only. */
.language-trigger-mobile::before {
  content: "";
  position: absolute;
  inset: -6px 0;
}

@media (min-width: 640px) {
  .language-trigger-mobile {
    font-size: 0.875rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}

.language-trigger-light {
  background: transparent;
  color: rgb(2 6 23);
}

.language-trigger-light:hover,
.language-trigger-light[aria-expanded='true'] {
  background: rgb(241 245 249);
}

.language-trigger-dark {
  background: transparent;
  color: rgb(248 250 252);
}

/* The home header server-renders the light tone; html.dark is set before the first paint. */
:global(.dark .language-trigger-light) {
  color: rgb(248 250 252);
}

.language-trigger-dark:hover,
.language-trigger-dark[aria-expanded='true'] {
  background: rgb(255 255 255 / 0.1);
}

.language-trigger:focus-visible {
  box-shadow: 0 0 0 3px rgb(34 211 238 / 0.35);
}

.language-overlay {
  align-items: flex-end;
  background:
    radial-gradient(circle at 18% 20%, rgb(216 234 241 / 0.92), transparent 34%),
    radial-gradient(circle at 72% 18%, rgb(242 237 249 / 0.9), transparent 38%),
    linear-gradient(135deg, rgb(236 242 247 / 0.96), rgb(243 238 249 / 0.96));
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 90;
}

.language-dialog-panel {
  background: white;
  border-radius: 10px 10px 0 0;
  box-shadow: 0 26px 80px rgb(15 23 42 / 0.12);
  color: rgb(15 23 42);
  max-height: 72vh;
  overflow: hidden;
  padding: 1.25rem 1.5rem 1.5rem;
  width: 100%;
}

.language-dialog-header {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 0.875rem;
}

.language-dialog-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
}

.language-close {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 9999px;
  color: rgb(15 23 42);
  cursor: pointer;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  outline: none;
  transition: background-color 150ms ease, box-shadow 150ms ease;
  width: 2.5rem;
}

.language-close:hover {
  background: rgb(248 250 252);
}

.language-close:focus-visible {
  box-shadow: 0 0 0 3px rgb(34 211 238 / 0.35);
}

.language-options {
  display: grid;
  gap: 0.25rem;
  grid-template-columns: 1fr;
  max-height: calc(72vh - 5.5rem);
  overflow-y: auto;
  padding-right: 0.25rem;
}

.language-option {
  align-items: center;
  border-radius: 8px;
  color: rgb(15 23 42);
  display: flex;
  font-size: 0.9375rem;
  font-weight: 500;
  justify-content: space-between;
  min-height: 2.75rem;
  padding: 0 0.75rem;
  text-decoration: none;
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease;
}

.language-option:hover,
.language-option:focus-visible {
  background: rgb(248 250 252);
  outline: none;
}

.language-option:focus-visible {
  box-shadow: 0 0 0 3px rgb(34 211 238 / 0.24);
}

.language-option-active {
  color: rgb(27 167 206);
}

@media (min-width: 640px) {
  .language-overlay {
    align-items: center;
    padding: 2rem;
  }

  .language-dialog-panel {
    border-radius: 10px;
    max-height: none;
    max-width: 1180px;
    padding: 2rem 2.75rem 2.25rem;
  }

  .language-dialog-header {
    margin-bottom: 1.25rem;
  }

  .language-options {
    gap: 0.25rem 4rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: none;
    overflow: visible;
  }
}

@media (min-width: 768px) {
  .language-options {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .language-options {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
